import { MonitoringCategory } from "@domain/product/monitoring/monitoring-product.service";
import { FgdsProductType } from "@domain/product/product";
import { Floorplan, ProductTypesInGroup } from "@domain/project/floorplan/floorplan";
import { FloorplanAlarmDevice } from "@domain/project/floorplan/floorplan-alarm-device";
import { FloorplanFlameDetector } from "@domain/project/floorplan/floorplan-flame-detector";
import { FloorplanFlameDetectorPlaceholder } from "@domain/project/floorplan/floorplan-flame-detector-placeholder";
import { FloorplanGasWarningCenter } from "@domain/project/floorplan/floorplan-gas-warning-center";
import { FloorplanGasWarningCenterPlaceholder } from "@domain/project/floorplan/floorplan-gas-warning-center-placeholder";
import { FloorplanPlasticSign } from "@domain/project/floorplan/floorplan-plastic-sign";
import { FloorplanSignalElement } from "@domain/project/floorplan/floorplan-signal-element";
import { FloorplanPlaceholderType, FloorplanSimplePlaceholder } from "@domain/project/floorplan/floorplan-simple-placeholder";
import { FloorplanTransmitter } from "@domain/project/floorplan/floorplan-transmitter";
import { FloorplanTransmitterPlaceholder } from "@domain/project/floorplan/floorplan-transmitter-placeholder";
import { FloorplanCentralStation } from "@domain/project/floorplan/monitoring/floorplan-central-station";
import { FloorplanDisplay } from "@domain/project/floorplan/monitoring/floorplan-display";
import { FloorplanKvm } from "@domain/project/floorplan/monitoring/floorplan-kvm";
import { FloorplanPatientMonitor } from "@domain/project/floorplan/monitoring/floorplan-patient-monitor";
import { FloorplanTelemetry } from "@domain/project/floorplan/monitoring/floorplan-telemetry";
import { FloorplanUtility } from "@domain/project/floorplan/monitoring/floorplan-utility";

/**
 * Returns all groupable product items from a floorplan in a flat list.
 */
export function getFloorplanItems(floorplan: Floorplan): ProductTypesInGroup[] {
  return [
    ...floorplan.gasWarningCenters,
    ...floorplan.gasWarningCenterPlaceholders,
    ...floorplan.transmitters,
    ...floorplan.transmitterPlaceholders,
    ...floorplan.alarmDevices,
    ...floorplan.alarmDevicePlaceholders,
    ...floorplan.signalElements,
    ...floorplan.signalElementPlaceholders,
    ...floorplan.plasticSigns,
    ...floorplan.plasticSignPlaceholders,
    ...floorplan.patientMonitors,
    ...floorplan.patientMonitorPlaceholders,
    ...floorplan.centralStations,
    ...floorplan.centralStationPlaceholders,
    ...floorplan.telemetries,
    ...floorplan.telemetryPlaceholders,
    ...floorplan.kvms,
    ...floorplan.kvmPlaceholders,
    ...floorplan.displays,
    ...floorplan.displayPlaceholders,
    ...floorplan.utilities,
    ...floorplan.utilityPlaceholders,
    ...floorplan.flameDetectors,
    ...floorplan.flameDetectorPlaceholders,
  ];
}

/**
 * Resolves the product kind (FgdsProductType or MonitoringCategory) for a given floorplan item.
 * Returns null if the item type is not recognized.
 */
export function getKindForItem(item: ProductTypesInGroup): FgdsProductType | MonitoringCategory | null {
  if (item instanceof FloorplanAlarmDevice) return FgdsProductType.ALARMDEVICE;
  if (item instanceof FloorplanGasWarningCenter) return FgdsProductType.GASWARNINGCENTER;
  if (item instanceof FloorplanGasWarningCenterPlaceholder) return FgdsProductType.GASWARNINGCENTER;
  if (item instanceof FloorplanPlasticSign) return FgdsProductType.PLASTICSIGN;
  if (item instanceof FloorplanSignalElement) return FgdsProductType.SIGNALELEMENT;
  if (item instanceof FloorplanTransmitter) return FgdsProductType.TRANSMITTER;
  if (item instanceof FloorplanTransmitterPlaceholder) return FgdsProductType.TRANSMITTER;
  if (item instanceof FloorplanFlameDetector) return FgdsProductType.FLAME_DETECTOR;
  if (item instanceof FloorplanFlameDetectorPlaceholder) return FgdsProductType.FLAME_DETECTOR;
  if (item instanceof FloorplanPatientMonitor) return MonitoringCategory.PATIENT_MONITORS;
  if (item instanceof FloorplanCentralStation) return MonitoringCategory.CENTRAL_STATIONS;
  if (item instanceof FloorplanTelemetry) return MonitoringCategory.TELEMETRY;
  if (item instanceof FloorplanKvm) return MonitoringCategory.KVMS;
  if (item instanceof FloorplanDisplay) return MonitoringCategory.DISPLAYS;
  if (item instanceof FloorplanUtility) return MonitoringCategory.UTILITY;
  if (item instanceof FloorplanSimplePlaceholder) {
    switch (item.type) {
      case FloorplanPlaceholderType.ALARM_DEVICE:
        return FgdsProductType.ALARMDEVICE;
      case FloorplanPlaceholderType.PLASTIC_SIGN:
        return FgdsProductType.PLASTICSIGN;
      case FloorplanPlaceholderType.SIGNAL_ELEMENT:
        return FgdsProductType.SIGNALELEMENT;
      case FloorplanPlaceholderType.PATIENT_MONITOR:
        return MonitoringCategory.PATIENT_MONITORS;
      case FloorplanPlaceholderType.CENTRAL_STATION:
        return MonitoringCategory.CENTRAL_STATIONS;
      case FloorplanPlaceholderType.TELEMETRY:
        return MonitoringCategory.TELEMETRY;
      case FloorplanPlaceholderType.KVM:
        return MonitoringCategory.KVMS;
      case FloorplanPlaceholderType.DISPLAY:
        return MonitoringCategory.DISPLAYS;
      case FloorplanPlaceholderType.UTILITY:
        return MonitoringCategory.UTILITY;
    }
  }
  return null;
}
